import React, { useEffect, useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Download,
  Save,
  X,
  Package,
  Truck,
  Calendar,
  CheckCircle,
  Clock,
  FileText
} from 'lucide-react';
import { purchaseReceiptsAPI, purchaseOrdersAPI, salesOrdersAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import {
  ResponsiveWrapper,
  ResponsiveSpacing,
  ResponsiveHeader,
  ResponsiveStats,
  ResponsiveCard,
  ResponsiveTableWrapper,
  ResponsiveFormWrapper,
  ResponsiveFormGrid,
  ResponsiveFooter,
  ResponsiveActions,
  ResponsiveSearchWrapper
} from '../components/ui/ResponsiveWrapper';
import { ResponsiveHybridTable } from '../components/ui/ResponsiveTable';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { productsAPI } from '../services/api';
import EntitySelectWithAdd from '../components/ui/EntitySelectWithAdd';
import { suppliersAPI } from '../services/api';

const defaultForm = {
  supplier_id: '',
  reference: '',
  date_validation: '',
  prix_achat: '',
  statut: 'pending',
  sales_order_id: '',
};

const AchatsReceptionCommandes = () => {
  const [receipts, setReceipts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [forceMobile, setForceMobile] = useState(false); // Debug option

  // Debug: Check screen size on mount
  useEffect(() => {
    console.log('Current screen width:', window.innerWidth);
    console.log('Current screen height:', window.innerHeight);
    console.log('User agent:', navigator.userAgent);
  }, []);

  // Fetch receipts
  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const res = await purchaseReceiptsAPI.getPurchaseReceipts();
      setReceipts(res.data);
      console.log('Fetched receipts:', res.data.length);
    } catch (err) {
      toast.error('Erreur lors du chargement des réceptions');
    }
    setLoading(false);
  };

  // Fetch suppliers for dropdown
  const fetchSuppliers = async () => {
    try {
      const res = await suppliersAPI.getSuppliers();
      setSuppliers(res.data);
    } catch (err) {
      // ignore
    }
  };

  // Fetch sales orders for dropdown
  const fetchSalesOrders = async () => {
    try {
      const res = await salesOrdersAPI.getSalesOrders();
      const orders = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setSalesOrders(orders);
    } catch (err) {
      console.error('Error fetching sales orders:', err);
    }
  };

  useEffect(() => {
    fetchReceipts();
    fetchSuppliers();
    fetchSalesOrders();
  }, []);

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Handle select changes
  const handleSelectChange = (name, selectedOption) => {
    setForm((f) => ({ ...f, [name]: selectedOption ? selectedOption.value : '' }));
  };

  // Handle add/edit submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      // Prepare form data, converting empty string to null for sales_order_id
      const formData = {
        ...form,
        sales_order_id: form.sales_order_id === '' ? null : form.sales_order_id
      };
      
      if (editingId) {
        await purchaseReceiptsAPI.updatePurchaseReceipt(editingId, formData);
        toast.success('Réception modifiée');
      } else {
        await purchaseReceiptsAPI.createPurchaseReceipt(formData);
        toast.success('Réception ajoutée');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchReceipts();
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
    }
    setSubmitLoading(false);
  };

  // Handle edit
  const handleEdit = (receipt) => {
    setForm({
      supplier_id: receipt.supplier_id?.toString() || '',
      reference: receipt.reference || '',
      date_validation: receipt.date_validation ? receipt.date_validation.slice(0, 10) : '',
      prix_achat: receipt.prix_achat || '',
      statut: receipt.statut || 'pending',
      sales_order_id: receipt.sales_order_id?.toString() || '',
    });
    setEditingId(receipt.id);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette réception ?')) {
      try {
        await purchaseReceiptsAPI.deletePurchaseReceipt(id);
        toast.success('Réception supprimée');
        fetchReceipts();
      } catch (err) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  // Handle add
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  const getSupplierInfo = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier ? supplier.name : 'Fournisseur inconnu';
  };

  const getSalesOrderInfo = (salesOrderId) => {
    if (!salesOrderId) return null;
    const salesOrder = salesOrders.find(so => so.id === salesOrderId);
    return salesOrder;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', variant: 'warning' },
      approved: { label: 'Approuvé', variant: 'success' },
      rejected: { label: 'Rejeté', variant: 'danger' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <ModernBadge variant={config.variant}>{config.label}</ModernBadge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const filteredReceipts = receipts.filter(receipt => {
    const supplierName = getSupplierInfo(receipt.supplier_id);
    const salesOrder = getSalesOrderInfo(receipt.sales_order_id);
    const clientName = salesOrder?.client_name || '';
    return String(supplierName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(receipt.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(receipt.prix_achat || '').includes(searchTerm) ||
           String(receipt.date_validation || '').includes(searchTerm) ||
           String(receipt.statut || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(clientName || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const tableColumns = [
    {
      key: 'supplier_id',
      label: 'Fournisseur',
      render: (value) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <Package className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{getSupplierInfo(value)}</div>
          </div>
        </div>
      )
    },
    {
      key: 'sales_order_id',
      label: 'Commande',
      render: (value, receipt) => {
        const salesOrder = getSalesOrderInfo(value);
        if (!salesOrder) {
          return <span className="text-neutral-400">-</span>;
        }
        return (
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="font-semibold text-neutral-900">#{salesOrder.id}</div>
              <div className="text-sm text-neutral-600">{salesOrder.client_name}</div>
              <div className="text-xs text-neutral-500">{formatCurrency(salesOrder.total || 0)}</div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'reference',
      label: 'Référence',
      render: (value) => (
        <span className="font-medium text-neutral-700">
          {value || '-'}
        </span>
      )
    },
    {
      key: 'date_validation',
      label: 'Date de validation',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{formatDate(value)}</span>
        </div>
      )
    },
    {
      key: 'prix_achat',
      label: 'Prix d\'achat',
      render: (value) => (
        <span className="font-semibold text-success-600">
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'statut',
      label: 'Statut',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, receipt) => (
        <ResponsiveActions>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(receipt)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(receipt.id)}
          >
            Supprimer
          </ModernButton>
        </ResponsiveActions>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  const totalAmount = receipts.reduce((sum, receipt) => sum + (Number(receipt.prix_achat) || 0), 0);

  return (
    <ResponsiveWrapper>
      <ResponsiveSpacing>
        {/* Debug Header - Temporary */}
        <ResponsiveCard className="bg-yellow-50 border-yellow-200">
      <div className="flex items-center justify-between">
        <div>
              <h3 className="text-sm font-semibold text-yellow-800">Debug Info</h3>
              <p className="text-xs text-yellow-600">
                Screen: {window.innerWidth}x{window.innerHeight} | 
                Receipts: {filteredReceipts.length} | 
                Force Mobile: {forceMobile ? 'ON' : 'OFF'}
              </p>
            </div>
            <ModernButton
              variant="warning"
              size="sm"
              onClick={() => setForceMobile(!forceMobile)}
            >
              {forceMobile ? 'Force Desktop' : 'Force Mobile'}
            </ModernButton>
          </div>
        </ResponsiveCard>

        {/* Header */}
        <ResponsiveHeader>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">Réception des commandes</h1>
            <p className="text-sm sm:text-base text-neutral-600">Gérez les réceptions de commandes d'achat</p>
        </div>
        <ModernButton
          variant="primary"
          icon={<Plus className="w-4 h-4" />}
          onClick={handleAdd}
        >
          Ajouter une réception
        </ModernButton>
        </ResponsiveHeader>

      {/* Stats Cards */}
        <ResponsiveStats>
          <ResponsiveCard>
          <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
            </div>
            <div>
                <p className="text-xs sm:text-sm font-medium text-neutral-600">Total des réceptions</p>
                <p className="text-xl sm:text-2xl font-bold text-neutral-900">{receipts.length}</p>
              </div>
            </div>
          </ResponsiveCard>

          <ResponsiveCard>
          <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-success-100 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-success-600" />
            </div>
            <div>
                <p className="text-xs sm:text-sm font-medium text-neutral-600">Montant total</p>
                <p className="text-xl sm:text-2xl font-bold text-success-600">{formatCurrency(totalAmount)}</p>
              </div>
            </div>
          </ResponsiveCard>

          <ResponsiveCard>
          <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-warning-100 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-warning-600" />
            </div>
            <div>
                <p className="text-xs sm:text-sm font-medium text-neutral-600">En attente</p>
                <p className="text-xl sm:text-2xl font-bold text-warning-600">
                {receipts.filter(r => r.statut === 'pending').length}
              </p>
            </div>
          </div>
          </ResponsiveCard>

          <ResponsiveCard>
          <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div>
                <p className="text-xs sm:text-sm font-medium text-neutral-600">Liées à des commandes</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-600">
                {receipts.filter(r => r.sales_order_id).length}
              </p>
            </div>
          </div>
          </ResponsiveCard>
        </ResponsiveStats>

      {/* Search and Table */}
        <ResponsiveCard>
          <ResponsiveHeader className="mb-4 sm:mb-6">
          <h2 className="text-lg font-semibold text-neutral-900">Liste des réceptions</h2>
            <ResponsiveSearchWrapper>
          <ModernSearchBar
            placeholder="Rechercher des réceptions..."
            onSearch={setSearchTerm}
                className="w-full sm:w-64"
              />
            </ResponsiveSearchWrapper>
          </ResponsiveHeader>

          {/* Force mobile view for testing */}
          {forceMobile ? (
            <div className="space-y-4">
              {filteredReceipts.map((receipt, index) => (
                <ModernCard key={index} className="p-4 hover:shadow-md transition-shadow">
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center mb-2 sm:mb-0">
                        <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider mr-2 sm:mr-4 min-w-[80px]">
                          Fournisseur:
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                            <Package className="w-5 h-5 text-primary-600" />
                          </div>
                          <div>
                            <div className="font-semibold text-neutral-900">{getSupplierInfo(receipt.supplier_id)}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center mb-2 sm:mb-0">
                        <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider mr-2 sm:mr-4 min-w-[80px]">
                          Commande:
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-semibold text-neutral-900">
                              {receipt.sales_order_id ? `#${receipt.sales_order_id}` : 'Aucune commande'}
                            </div>
                            <div className="text-sm text-neutral-600">
                              {receipt.sales_order_id ? getSalesOrderInfo(receipt.sales_order_id)?.client_name || 'Client inconnu' : 'Aucune commande associée'}
                            </div>
                            <div className="text-xs text-neutral-500">
                              {receipt.sales_order_id ? formatCurrency(getSalesOrderInfo(receipt.sales_order_id)?.total || 0) : '0.00'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center mb-2 sm:mb-0">
                        <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider mr-2 sm:mr-4 min-w-[80px]">
                          Référence:
                        </span>
                      </div>
                      <div className="flex-1">
                        <span className="font-medium text-neutral-700">
                          {receipt.reference || '-'}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center mb-2 sm:mb-0">
                        <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider mr-2 sm:mr-4 min-w-[80px]">
                          Date:
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
                          <span className="text-sm">{formatDate(receipt.date_validation)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center mb-2 sm:mb-0">
                        <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider mr-2 sm:mr-4 min-w-[80px]">
                          Prix:
                        </span>
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold text-success-600">
                          {formatCurrency(receipt.prix_achat)}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center mb-2 sm:mb-0">
                        <span className="text-xs font-semibold text-neutral-600 uppercase tracking-wider mr-2 sm:mr-4 min-w-[80px]">
                          Statut:
                        </span>
                      </div>
                      <div className="flex-1">
                        {getStatusBadge(receipt.statut)}
                      </div>
                    </div>

                    <div className="pt-3 border-t border-neutral-200">
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <ResponsiveActions>
                          <ModernButton
                            variant="primary"
                            size="sm"
                            icon={<Edit className="w-4 h-4" />}
                            onClick={() => handleEdit(receipt)}
                          >
                            Modifier
                          </ModernButton>
                          <ModernButton
                            variant="danger"
                            size="sm"
                            icon={<Trash2 className="w-4 h-4" />}
                            onClick={() => handleDelete(receipt.id)}
                          >
                            Supprimer
                          </ModernButton>
                        </ResponsiveActions>
                      </div>
                    </div>
                  </div>
                </ModernCard>
              ))}
            </div>
          ) : (
            /* Automatic responsive table - cards on mobile, table on desktop */
            <ResponsiveHybridTable
          columns={tableColumns}
          data={filteredReceipts}
          className="w-full"
              cardViewBreakpoint="lg" // More aggressive - switches to cards below 1024px
        />
          )}
        </ResponsiveCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier la réception' : 'Ajouter une réception'}
        size="full"
      >
          <ResponsiveFormWrapper>
            {editingId && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-blue-800">Informations actuelles de la réception</h4>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white p-4 rounded-lg border border-blue-100">
                    <div className="flex items-center mb-2">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                        <Truck className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="font-semibold text-green-700 text-sm">Fournisseur actuel</span>
                    </div>
                    <div className="text-green-900 font-medium">
                      {getSupplierInfo(receipts.find(r => r.id === editingId)?.supplier_id)}
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-blue-100">
                    <div className="flex items-center mb-2">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                        <FileText className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="font-semibold text-purple-700 text-sm">Commande liée</span>
                    </div>
                    {(() => {
                      const currentReceipt = receipts.find(r => r.id === editingId);
                      const salesOrder = getSalesOrderInfo(currentReceipt?.sales_order_id);
                      if (salesOrder) {
                        return (
                          <div className="space-y-1">
                            <div className="text-purple-900 font-medium">Commande #{salesOrder.id}</div>
                            <div className="text-sm text-purple-700">{salesOrder.client_name}</div>
                            <div className="text-sm text-purple-600 font-semibold">
                              {formatCurrency(salesOrder.total || 0)}
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <div className="text-purple-600 italic">Aucune commande liée</div>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                <ResponsiveFormGrid cols={2}>
                  <ModernSelect
                    label="Fournisseur"
                    name="supplier_id"
                    value={suppliers.map(s => ({ value: s.id.toString(), label: s.name })).find(opt => opt.value === form.supplier_id) || null}
                    onChange={(selectedOption) => handleSelectChange('supplier_id', selectedOption)}
                    options={suppliers.map(s => ({ value: s.id.toString(), label: s.name }))}
                    placeholder="Sélectionner un fournisseur"
                    required
                  />

                  <ModernSelect
                    label="Commande de vente"
                    name="sales_order_id"
                    value={salesOrders.map(so => ({ value: so.id.toString(), label: `Commande #${so.id} - ${so.client_name}` })).find(opt => opt.value === form.sales_order_id) || null}
                    onChange={(selectedOption) => handleSelectChange('sales_order_id', selectedOption)}
                    options={[
                      { value: '', label: 'Aucune commande' },
                      ...salesOrders.map(so => ({ value: so.id.toString(), label: `Commande #${so.id} - ${so.client_name} (${formatCurrency(so.total || 0)})` }))
                    ]}
                    placeholder="Sélectionner une commande de vente (optionnel)"
                  />

                  <ModernInput
                    label="Référence"
                    name="reference"
                    type="text"
                    value={form.reference}
                    onChange={handleChange}
                    placeholder="Entrez la référence"
                    required
                  />

                  <ModernInput
                    label="Date de validation"
                    name="date_validation"
                    type="date"
                    value={form.date_validation}
                    onChange={handleChange}
                    icon={<Calendar className="w-5 h-5" />}
                    required
                  />

                  <ModernInput
                    label="Prix d'achat"
                    name="prix_achat"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.prix_achat}
                    onChange={handleChange}
                    placeholder="0.00"
                    required
                  />

                  <ModernSelect
                    label="Statut"
                    name="statut"
                    value={form.statut ? { value: form.statut, label: form.statut === 'pending' ? 'En attente' : form.statut === 'approved' ? 'Approuvé' : 'Rejeté' } : null}
                    onChange={(selectedOption) => handleSelectChange('statut', selectedOption)}
                    options={[
                      { value: 'pending', label: 'En attente' },
                      { value: 'approved', label: 'Approuvé' },
                      { value: 'rejected', label: 'Rejeté' }
                    ]}
                    required
                  />
                </ResponsiveFormGrid>
              </div>

              <ResponsiveFooter className="pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
              </ResponsiveFooter>
        </form>
          </ResponsiveFormWrapper>
      </ModernModal>
      </ResponsiveSpacing>
    </ResponsiveWrapper>
  );
};

export default AchatsReceptionCommandes; 